/**
 * LA MODALE DEL CHECK-IN D'INCONTRO — quando la barra non basta a farsi vedere.
 *
 * 🔴 **La richiesta** (Luca, 2026-09-26T11:20:53Z, allo sportello, su L-2273), testuale: «quando la
 * partita viene trovata e si deve fare checkin al match, fai venire fuori modale più impattante per
 * richiamare attenzione con al countdown». Il check-in dura poco (qCrC: `matchCheckInMinutes = 1`) e un
 * incontro perso e' una sconfitta a tavolino: la barra da sola, nel collaudo delle code, non bastava.
 *
 * **Derivazioni del cantiere** (tma-orcb-11-modale-checkin, disegno approvato da tma-orcb 11:26:04Z):
 *  · compare al primo arrivo di un check-in d'incontro DA FARE (l'azione `checkin-incontro` della barra,
 *    cioe' la voce `match-checkin` actionable del motore), una volta per incontro (`tournamentId:matchId`);
 *  · piu' check-in insieme ⇒ UNA modale, sul primo nell'ordine delle azioni (quello del motore: urgenza,
 *    poi scadenza), e all'apertura si consumano TUTTI i presenti — niente catena di modali: gli altri li
 *    porta la barra;
 *  · sulla scheda di QUELL'incontro non compare, e la chiave si consuma: li' c'e' «Sono pronto»;
 *  · con una partita real-time viva non compare e la chiave NON si consuma (I-174: sopra la plancia
 *    Luca ha voluto solo il bottone flottante): arriva a partita finita, se il check-in e' ancora aperto;
 *  · sparisce quando l'azione non c'e' piu' (check-in fatto ⇒ `awaiting-opponent`, o incontro chiuso).
 * ⛔ Nessun ramo guarda l'orologio (come la barra): a zero la modale resta, e la chiude il server.
 *
 * ⚠️ Pura e senza `import.meta.env`: le funzioni ricevono la memoria, non la leggono.
 */
import { destinazioneDi, type LimuloAzionePendente } from './azionePersistente.ts';

/**
 * La memoria della SESSIONE: le chiavi gia' consumate. In modulo, non in `sessionStorage` (disegno
 * approvato, punto 3): un reload con il check-in ancora aperto la azzera e la modale RICOMPARE — in
 * Telegram chiudere e riaprire la mini app e' un reload, e chi rientra con un check-in aperto e'
 * esattamente chi la modale deve prendere. Sopravvive invece a uno smontaggio della barra (logout a
 * parte, che ricarica). I banchi la svuotano con `.clear()`.
 */
export const modaliCheckInConsumate = new Set<string>();

/** La chiave della modale: l'INCONTRO, non l'azione — un incontro ha un solo check-in. */
export const chiaveModaleCheckIn = (a: Pick<LimuloAzionePendente, 'tournamentId' | 'matchId'>): string =>
  `${a.tournamentId}:${a.matchId ?? '-'}`;

const eCheckIn = (a: LimuloAzionePendente): boolean => a.tipo === 'checkin-incontro' && a.matchId !== null;

export interface EsitoModaleCheckIn {
  /** Il check-in da mostrare adesso, o `null`. */
  mostra: LimuloAzionePendente | null;
  /** Quanti ALTRI check-in ci sono insieme a quello mostrato (la riga «e altri N»). */
  altri: number;
  /** Le chiavi da segnare come consumate, che la modale si apra o no. */
  consuma: string[];
}

/**
 * Si apre una modale, adesso? Da chiamare solo quando non ce n'e' gia' una aperta.
 * @param consumate le chiavi gia' mostrate (o gia' viste sulla loro scheda) in questa sessione.
 * @param pathname la rotta attuale.
 * @param partitaViva c'e' una partita real-time in corso (`descriptor` di `ActiveMatchContext`).
 */
export function modaleCheckInDaAprire(input: {
  azioni: readonly LimuloAzionePendente[];
  consumate: ReadonlySet<string>;
  pathname: string;
  partitaViva: boolean;
}): EsitoModaleCheckIn {
  const nessuna: EsitoModaleCheckIn = { mostra: null, altri: 0, consuma: [] };
  if (input.partitaViva) return nessuna;
  const checkIn = input.azioni.filter(eCheckIn);
  const sullaScheda = checkIn.filter((a) => destinazioneDi(a) === input.pathname).map(chiaveModaleCheckIn);
  const nuovi = checkIn.filter(
    (a) => !input.consumate.has(chiaveModaleCheckIn(a)) && destinazioneDi(a) !== input.pathname,
  );
  const [primo] = nuovi;
  if (primo === undefined) {
    return { ...nessuna, consuma: sullaScheda.filter((k) => !input.consumate.has(k)) };
  }
  return {
    mostra: primo,
    altri: checkIn.length - 1,
    consuma: checkIn.map(chiaveModaleCheckIn).filter((k) => !input.consumate.has(k)),
  };
}

/**
 * La modale aperta su `chiave` resta aperta? Si', finche' il suo check-in e' fra le azioni, non si e'
 * sulla sua scheda e non e' cominciata una partita viva. Rende l'azione AGGIORNATA (la scadenza puo'
 * cambiare fra un giro e l'altro), o `null` = si chiude.
 */
export function modaleCheckInAncoraAperta(input: {
  chiave: string;
  azioni: readonly LimuloAzionePendente[];
  pathname: string;
  partitaViva: boolean;
}): LimuloAzionePendente | null {
  if (input.partitaViva) return null;
  const a = input.azioni.find((x) => eCheckIn(x) && chiaveModaleCheckIn(x) === input.chiave);
  if (a === undefined || destinazioneDi(a) === input.pathname) return null;
  return a;
}
